import { Server, Clock, Users, Activity } from 'lucide-react'
import { EmptyState } from './StatusIndicator'
import { formatUptime } from './format'
import type { SmartDeviceRow, SystemInfo } from '../../types/monitoring'

export function SystemInfoPanel({ system, loading }: { system: SystemInfo | undefined; loading: boolean }) {
  const bootDate = system?.boot_time ? new Date(system.boot_time * 1000) : null

  const items = [
    { icon: <Server className="w-4 h-4 text-neutral-400" />, label: 'Hostname', value: system?.hostname || '—' },
    { icon: <Clock className="w-4 h-4 text-neutral-400" />, label: 'Uptime', value: formatUptime(system?.uptime_seconds) },
    { icon: <Clock className="w-4 h-4 text-neutral-400" />, label: 'Boot time', value: bootDate ? bootDate.toLocaleString() : '—' },
    { icon: <Users className="w-4 h-4 text-neutral-400" />, label: 'Logged-in users', value: system?.logged_in_users ?? '—' },
    { icon: <Activity className="w-4 h-4 text-neutral-400" />, label: 'Running processes', value: system?.process_count?.toLocaleString() ?? '—' },
  ]

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6">
      <h2 className="font-bold text-secondary mb-4">System Information</h2>
      {loading ? (
        <div className="h-40 bg-neutral-100 rounded-lg animate-pulse" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {items.map((item) => (
            <div key={item.label} className="flex items-center gap-3 py-2 px-3 rounded-xl bg-neutral-50">
              {item.icon}
              <div className="min-w-0">
                <p className="text-xs text-neutral-400">{item.label}</p>
                <p className="text-sm font-semibold text-neutral-800 truncate">{item.value}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function SmartPanel({ devices, loading }: { devices: SmartDeviceRow[]; loading: boolean }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6">
      <h2 className="font-bold text-secondary mb-1">Disk SMART Health</h2>
      <p className="text-xs text-neutral-400 mb-4">Updated roughly every 30 minutes — SMART data changes slowly</p>
      {loading ? (
        <div className="h-40 bg-neutral-100 rounded-lg animate-pulse" />
      ) : !devices.length ? (
        <EmptyState message="SMART data unavailable — smartmontools may not be installed, or the collector lacks block device access." />
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm border-separate border-spacing-0">
            <thead>
              <tr className="text-left">
                <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide">Device</th>
                <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide">Model</th>
                <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide">Health</th>
                <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide text-right">Power-On Hours</th>
                <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide text-right">Reallocated Sectors</th>
                <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide text-right">Temp</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <tr key={d.device} className="hover:bg-neutral-50 transition-colors">
                  <td className="py-2.5 px-2 border-t border-neutral-100 font-medium text-neutral-800">{d.device}</td>
                  <td className="py-2.5 px-2 border-t border-neutral-100 text-neutral-500 truncate max-w-[160px]">{d.model}</td>
                  <td className="py-2.5 px-2 border-t border-neutral-100">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      d.health_passed ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {d.health_passed ? 'Passed' : 'Failed'}
                    </span>
                  </td>
                  <td className="py-2.5 px-2 border-t border-neutral-100 text-right text-neutral-600 tabular-nums">{d.power_on_hours ?? '—'}</td>
                  <td className="py-2.5 px-2 border-t border-neutral-100 text-right text-neutral-600 tabular-nums">
                    <span className={d.reallocated_sectors ? 'text-red-600 font-semibold' : ''}>{d.reallocated_sectors ?? 0}</span>
                  </td>
                  <td className="py-2.5 px-2 border-t border-neutral-100 text-right text-neutral-600 tabular-nums">{d.temperature_c != null ? `${d.temperature_c}°C` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
